import axios from "axios";
import { strings, paths } from "../utils/constants";
import { getToken } from "./API";
import { Toast } from "../component/Toast";

/**
 * On an expired/invalid admin session, clear admin auth and send the user
 * back to the admin login. Uses a hard redirect (window.location) because
 * this module is outside the React Router tree. Cookies are cleared so the
 * App-level auth watcher does not immediately bounce back.
 */
const handleUnauthorized = () => {
  try {
    localStorage.removeItem("adminToken");
    document.cookie =
      "admin_access_token=; path=/admin/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie =
      "admin_refresh_token=; path=/admin/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  } catch (_) {
    /* ignore storage errors */
  }
  if (!window.location.pathname.endsWith(paths.adminRoutes.ADMIN)) {
    window.location.assign(paths.adminRoutes.ADMIN);
  }
};

/**
 * Video Management API service.
 *
 * This module mirrors the existing FormDepot admin API conventions
 * (see services/API.js): it uses the same base URL, the same admin token
 * retrieval (getToken(true)) and the same Toast-based error surfacing.
 *
 * A dedicated axios instance is used (instead of the generic buildEndpoint
 * helpers) because the video feature has several REST-style sub-routes
 * (/admin/videos/:id/play-url, etc.) and, most importantly, because the
 * actual binary upload must go DIRECTLY to the pre-signed S3 URL and must
 * NOT pass through the FormDepot backend or carry the Authorization header.
 *
 * Security notes:
 *  - The React app never receives or stores AWS credentials.
 *  - S3 keys are always generated by the backend.
 *  - Pre-signed URLs are used transiently and never persisted.
 */

// Language prefix used across the admin panel (matches strings.language.EN).
const LANG = strings.language.EN;

// Base path for all video admin endpoints, e.g. /en/admin/videos
const VIDEO_BASE = `${LANG}/admin/videos`;

// Normalize the configured endpoint so the API base path (e.g. "/api") is
// never dropped. axios/URL resolution drops the last path segment of a base
// URL that has no trailing slash when the request path is relative, so
// "http://localhost:8000/api" + "en/admin/videos" would wrongly resolve to
// "http://localhost:8000/en/admin/videos". Ensuring a single trailing slash
// here makes the video endpoints work whether or not the env var includes one.
const normalizeBaseUrl = (url) => {
  if (!url) return url;
  return url.endsWith("/") ? url : `${url}/`;
};

// Shared axios instance that points at the FormDepot backend.
const videoAxios = axios.create({
  baseURL: normalizeBaseUrl(process.env.REACT_APP_API_ENDPOINT),
  timeout: 30000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

const authHeaders = () => ({
  Authorization: `Bearer ${getToken(true)}`,
  "Content-Type": "application/json",
});

/**
 * Base path for the PUBLIC video read endpoints, honoring the current UI
 * language, e.g. /en/admin/videos or /fr/admin/videos.
 * The GET endpoints (list, single, play-url) are public (no auth), so these
 * helpers send no Authorization header.
 */
const publicVideoBase = (language) => {
  const lang = language === strings.language.FR ? strings.language.FR : strings.language.EN;
  return `${lang}/admin/videos`;
};

const publicHeaders = () => ({ "Content-Type": "application/json" });

/**
 * Error handler for the PUBLIC promo-page read calls. Unlike the admin
 * handleError, this is SILENT: it never shows a toast and never redirects to
 * login. The promotional page is a public page — a failed/401 video request
 * must not surface a "Not authorized" toast or bounce the visitor to login.
 * It simply returns a failed result so the page renders its empty state.
 */
const handlePublicError = (e) => {
  return {
    success: false,
    message: e?.response?.data?.message || e?.message || "",
  };
};

/**
 * PUBLIC GET /{lang}/admin/videos — list videos for the promotional page.
 * Optional params: { page, limit, status }. Returns the raw response envelope
 * { success, page, total, message, data: { items, pagination } } (or a
 * normalized error object from handleError).
 */
export const getPublicVideos = async (language, params = {}) => {
  try {
    const query = new URLSearchParams(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
      )
    ).toString();
    const base = publicVideoBase(language);
    const url = query ? `${base}?${query}` : base;
    const { data } = await videoAxios.get(url, { headers: publicHeaders() });
    return data;
  } catch (e) {
    return handlePublicError(e);
  }
};

/** Pull a playback URL out of the various response shapes. */
const extractPlayUrl = (data) => {
  const src = data?.data || data?.result || data || {};
  return (
    src.playUrl ||
    src.play_url ||
    src.url ||
    src.signedUrl ||
    src.presignedUrl ||
    src.presigned_url
  );
};

/**
 * PUBLIC playback URL for a given video language.
 *  - videoLang "fr" -> GET /{lang}/admin/videos/:id/play-url-fr
 *  - otherwise       -> GET /{lang}/admin/videos/:id/play-url  (English)
 * `language` only controls response-message language; `videoLang` selects the
 * actual video file. Normalizes to a top-level { playUrl }.
 */
export const getPublicVideoPlayUrl = async (language, id, videoLang = "en") => {
  const suffix = videoLang === strings.language.FR ? "play-url-fr" : "play-url";
  const path = `${publicVideoBase(language)}/${id}/${suffix}`;
  try {
    const { data } = await videoAxios.get(path, { headers: publicHeaders() });
    return { playUrl: extractPlayUrl(data), raw: data };
  } catch (e) {
    return handlePublicError(e);
  }
};

/**
 * Central error handler. Surfaces a user-friendly Toast and returns a
 * normalized error object so callers/thunks can react. Never leaks AWS
 * internals to the UI.
 */
const handleError = (e, fallbackTitle = strings.words.ERROR) => {
  if (e?.code === strings.error.NETWORK_ERROR) {
    Toast({ type: "error", title: e.message, message: e.message });
    return { success: false, message: e.message };
  }

  const status = e?.response?.status;
  if (status === 401) {
    Toast({
      type: "error",
      title: `${status} ${e.response.statusText}`,
      message: strings.error.NOT_AUTHORIZED_ERROR,
    });
    // Spec: 401 -> token expired/invalid -> redirect to login.
    handleUnauthorized();
    return { success: false, message: strings.error.NOT_AUTHORIZED_ERROR };
  }

  const message =
    e?.response?.data?.errors?.[0]?.message ||
    e?.response?.data?.message ||
    "Something went wrong. Please try again.";
  Toast({
    type: "error",
    title: status ? `${status} ${e.response.statusText}` : fallbackTitle,
    message,
  });
  return { success: false, message };
};

/**
 * GET /admin/videos
 * Optional pagination/search params are appended as a query string.
 */
export const getVideos = async (params = {}) => {
  try {
    const query = new URLSearchParams(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
      )
    ).toString();
    const url = query ? `${VIDEO_BASE}?${query}` : VIDEO_BASE;
    const { data } = await videoAxios.get(url, { headers: authHeaders() });
    return data;
  } catch (e) {
    return handleError(e);
  }
};

/**
 * GET /admin/videos/:id
 */
export const getVideo = async (id) => {
  try {
    const { data } = await videoAxios.get(`${VIDEO_BASE}/${id}`, {
      headers: authHeaders(),
    });
    return data;
  } catch (e) {
    return handleError(e);
  }
};

/**
 * Normalize a pre-signed URL response into { uploadUrl, s3Key }.
 * Different backends nest/name these differently, so we accept common
 * variants: top-level or under `data`/`result`, and url/key aliases.
 */
const normalizeUploadUrlResponse = (data) => {
  const src = data?.data || data?.result || data || {};
  const uploadUrl =
    src.uploadUrl ||
    src.uploadURL ||
    src.upload_url ||
    src.url ||
    src.signedUrl ||
    src.presignedUrl ||
    src.presigned_url;
  const s3Key =
    src.s3Key ||
    src.s3_key ||
    src.key ||
    src.Key ||
    src.objectKey ||
    src.fileKey;
  return { uploadUrl, s3Key, raw: data };
};

/**
 * Step 1 of upload: POST /admin/videos/upload-url
 * body: { fileName, contentType }
 * returns: { uploadUrl, s3Key }
 */
export const requestVideoUploadUrl = async (body) => {
  try {
    const { data } = await videoAxios.post(`${VIDEO_BASE}/upload-url`, body, {
      headers: authHeaders(),
    });
    return normalizeUploadUrlResponse(data);
  } catch (e) {
    return handleError(e);
  }
};

/**
 * Step 2 of upload: PUT the file DIRECTLY to the pre-signed S3 URL.
 * No Authorization header is sent to S3. Progress is reported via callback.
 * Returns { success: true } on 2xx.
 */
export const uploadToS3 = async (uploadUrl, file, onProgress) => {
  try {
    // The pre-signed URL only enforces the headers listed in its
    // `X-Amz-SignedHeaders` query param. If it did NOT sign `content-type`,
    // sending a Content-Type header will cause a 403 SignatureDoesNotMatch.
    // So only send Content-Type when the URL was signed with it.
    let signedHeaders = "";
    try {
      signedHeaders = (
        new URL(uploadUrl).searchParams.get("X-Amz-SignedHeaders") || ""
      ).toLowerCase();
    } catch (_) {
      // ignore URL parsing issues; fall back to no extra headers
    }

    const putHeaders = signedHeaders.includes("content-type")
      ? { "Content-Type": file.type || "video/mp4" }
      : {};

    // Use a bare axios instance so no default JSON headers/interceptors leak
    // into the S3 request.
    await axios.put(uploadUrl, file, {
      headers: putHeaders,
      timeout: 0, // allow large uploads
      transformRequest: [(data) => data], // send the File as-is
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return { success: true };
  } catch (e) {
    // Log the real S3 error to the console for diagnosis (CORS, 403 signature
    // mismatch, expired URL, etc.) without leaking it to the UI.
    // eslint-disable-next-line no-console
    console.error(
      "[VideoUpload] S3 PUT failed:",
      e?.response?.status,
      e?.response?.data,
      e?.message
    );
    Toast({
      type: "error",
      title: strings.words.ERROR,
      message: strings.admin.error.VIDEO_UPLOAD_FAILED,
    });
    return { success: false, message: strings.admin.error.VIDEO_UPLOAD_FAILED };
  }
};

/**
 * Step 3 of upload: POST /admin/videos (save metadata).
 * body: { title, fileName, s3Key, contentType, fileSize, duration }
 */
export const createVideo = async (body) => {
  try {
    const { data } = await videoAxios.post(VIDEO_BASE, body, {
      headers: authHeaders(),
    });
    return data;
  } catch (e) {
    return handleError(e);
  }
};

/**
 * PUT /admin/videos/:id
 * body: { title, status? }
 */
export const updateVideo = async (id, body) => {
  try {
    const { data } = await videoAxios.put(`${VIDEO_BASE}/${id}`, body, {
      headers: authHeaders(),
    });
    return data;
  } catch (e) {
    return handleError(e);
  }
};

/**
 * Replace step 1: POST /admin/videos/:id/replace-url
 * body: { fileName, contentType }
 * returns: { uploadUrl, s3Key }
 */
export const requestVideoReplaceUrl = async (id, body) => {
  try {
    const { data } = await videoAxios.post(
      `${VIDEO_BASE}/${id}/replace-url`,
      body,
      { headers: authHeaders() }
    );
    return normalizeUploadUrlResponse(data);
  } catch (e) {
    return handleError(e);
  }
};

/**
 * Replace step 3: POST /admin/videos/:id/replace
 * Tells the backend the S3 upload finished. Backend swaps the DB record and
 * removes the old S3 object. body: { s3Key, fileName, contentType, fileSize, duration }
 */
export const completeVideoReplacement = async (id, body) => {
  try {
    const { data } = await videoAxios.post(`${VIDEO_BASE}/${id}/replace`, body, {
      headers: authHeaders(),
    });
    return data;
  } catch (e) {
    return handleError(e);
  }
};

/**
 * French replace step 1: POST /admin/videos/:id/replace-url-fr
 * body: { fileName, contentType } -> { uploadUrl, s3Key }
 * Also used to ADD a French video to a record created with English only.
 */
export const requestVideoReplaceUrlFr = async (id, body) => {
  try {
    const { data } = await videoAxios.post(
      `${VIDEO_BASE}/${id}/replace-url-fr`,
      body,
      { headers: authHeaders() }
    );
    return normalizeUploadUrlResponse(data);
  } catch (e) {
    return handleError(e);
  }
};

/**
 * French replace step 3: POST /admin/videos/:id/replace-fr
 * body: { fileName, s3Key, contentType, fileSize, duration } -> updated record.
 * The English file is untouched. This also adds French to an English-only record.
 */
export const completeVideoReplacementFr = async (id, body) => {
  try {
    const { data } = await videoAxios.post(
      `${VIDEO_BASE}/${id}/replace-fr`,
      body,
      { headers: authHeaders() }
    );
    return data;
  } catch (e) {
    return handleError(e);
  }
};

/**
 * GET /admin/videos/:id/play-url
 * returns: { playUrl } - a temporary signed GET URL for playback.
 *
 * The backend wraps success responses as { success, data }, so the URL may be
 * at data.playUrl. We normalize to always return a top-level { playUrl }.
 */
export const getVideoPlayUrl = async (id, videoLang = "en") => {
  const suffix = videoLang === strings.language.FR ? "play-url-fr" : "play-url";
  try {
    const { data } = await videoAxios.get(`${VIDEO_BASE}/${id}/${suffix}`, {
      headers: authHeaders(),
    });
    return { playUrl: extractPlayUrl(data), raw: data };
  } catch (e) {
    return handleError(e);
  }
};

/**
 * DELETE /admin/videos/:id
 * Backend is responsible for removing the S3 object and DB record.
 */
export const deleteVideo = async (id) => {
  try {
    const { data } = await videoAxios.delete(`${VIDEO_BASE}/${id}`, {
      headers: authHeaders(),
    });
    return data;
  } catch (e) {
    return handleError(e);
  }
};
